import { Button } from '@citizen-walk/shared-ui';
import {
  SURVEY_QUESTION_TYPES,
  SURVEY_QUESTION_TYPE_LABELS,
  changeQuestionType,
  type SurveyOption,
  type SurveyQuestion,
  type SurveyQuestionType,
} from '@citizen-walk/shared-types';
import { useState } from 'react';
import { surveyOptionsFromText, surveyOptionsToText } from './surveyOptionsText';

/**
 * 設問 visual editor (AW8、docs/108 Critical #10/#11)。
 *
 * 旧2 `old2/.../apps/admin/src/pages/SurveyEditorPage.tsx` の `QuestionCard` /
 * `QuestionTypeFields` / `ChoiceOptionsEditor` (L300-646) を v2 で踏襲。
 * SurveyEditPage (地区) と SurveyPresetEditPage (master) の両方で再利用し、生 JSON textarea を
 * 既定エディタから置換する (JSON は折りたたみの advanced fallback として残す)。
 *
 * 踏襲した点:
 *   - 質問文 / タイプ select / 補足説明 / 必須 toggle
 *   - short_text/long_text: プレースホルダ + 最大文字数
 *   - radio/dropdown/checkbox: 選択肢 add/remove + (checkbox) min/maxSelected + (radio/checkbox) allowOther
 *   - linear_scale: min/max/minLabel/maxLabel
 *   - rating: 星の最大数 (3/5/7/10)
 *   - number: min/max/step/unit
 *   - 上下移動 / 削除 / type 変更 (changeQuestionType で id/title/description/required 維持)
 *
 * 変えた点:
 *   - shared-ui Button + Tailwind preset の色 token (neutral→slate, brand→primary, error→danger)
 *   - JSON 直接編集を「上級者向け」collapsible に格下げ (既定は visual)
 *   - inline style 不使用 (CLAUDE.md デザインシステム準拠)
 */

const inputCls = 'w-full min-h-touch px-3 border border-slate-300 rounded text-sm bg-white';
const labelCls = 'text-xs text-slate-500 block mb-1';

/** radio / dropdown / checkbox の選択肢エディタ。 */
function ChoiceOptionsEditor({
  question,
  disabled,
  onChange,
}: {
  question: Extract<SurveyQuestion, { options: SurveyOption[] }>;
  disabled: boolean;
  onChange: (q: SurveyQuestion) => void;
}) {
  // 入力中の文字列は手元に持つ (保存値から毎回作り直すと、末尾の改行や空行が消えて改行できない)。
  // 選択肢が外から変わったとき (JSON 編集・タイプ変更など) だけ入力中の文字列を差し替える。
  const external = surveyOptionsToText(question.options);
  const [draft, setDraft] = useState(external);
  const normalized = surveyOptionsToText(surveyOptionsFromText(draft));
  if (normalized !== external) setDraft(external);

  return (
    <div>
      <label className="block">
        <span className={labelCls}>選択肢（1 行に 1 つ。まとめて貼り付けもできます）</span>
        <textarea
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            onChange({
              ...question,
              options: surveyOptionsFromText(e.target.value),
            });
          }}
          disabled={disabled}
          rows={Math.min(20, Math.max(3, question.options.length + 2))}
          className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white font-mono"
        />
      </label>
      {question.options.length === 0 && (
        <p className="text-xs text-danger-700 mt-1">選択肢を 1 つ以上入力してください。</p>
      )}
      <p className="text-[10px] text-slate-500 mt-1">
        回答には選んだ選択肢の文言がそのまま記録されます。回答が集まった後に文言を直すと、直す前の回答は元の文言のまま残ります。
      </p>

      {question.type === 'checkbox' && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="block">
            <span className={labelCls}>最小選択数</span>
            <input
              type="number"
              min={0}
              value={question.minSelected ?? ''}
              onChange={(e) =>
                onChange({
                  ...question,
                  minSelected: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              disabled={disabled}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>最大選択数</span>
            <input
              type="number"
              min={0}
              value={question.maxSelected ?? ''}
              onChange={(e) =>
                onChange({
                  ...question,
                  maxSelected: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              disabled={disabled}
              className={inputCls}
            />
          </label>
        </div>
      )}

      {(question.type === 'radio' || question.type === 'checkbox') && (
        <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={question.allowOther ?? false}
            onChange={(e) => onChange({ ...question, allowOther: e.target.checked })}
            disabled={disabled}
            className="w-4 h-4"
          />
          「その他(自由記述)」を許可する
        </label>
      )}
    </div>
  );
}

/** 質問タイプ別の詳細フィールド (旧2 QuestionTypeFields 踏襲)。 */
function QuestionTypeFields({
  question,
  disabled,
  onChange,
}: {
  question: SurveyQuestion;
  disabled: boolean;
  onChange: (q: SurveyQuestion) => void;
}) {
  switch (question.type) {
    case 'short_text':
    case 'long_text':
      return (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className={labelCls}>プレースホルダ</span>
            <input
              value={question.placeholder ?? ''}
              onChange={(e) => onChange({ ...question, placeholder: e.target.value })}
              disabled={disabled}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>最大文字数(任意)</span>
            <input
              type="number"
              min={1}
              value={question.maxLength ?? ''}
              onChange={(e) =>
                onChange({
                  ...question,
                  maxLength: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              disabled={disabled}
              className={inputCls}
            />
          </label>
        </div>
      );
    case 'radio':
    case 'dropdown':
    case 'checkbox':
      return <ChoiceOptionsEditor question={question} disabled={disabled} onChange={onChange} />;
    case 'linear_scale':
      return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <label className="block">
            <span className={labelCls}>最小値</span>
            <input
              type="number"
              value={question.min}
              onChange={(e) => onChange({ ...question, min: Number(e.target.value) })}
              disabled={disabled}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>最大値</span>
            <input
              type="number"
              value={question.max}
              onChange={(e) => onChange({ ...question, max: Number(e.target.value) })}
              disabled={disabled}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>最小ラベル</span>
            <input
              value={question.minLabel ?? ''}
              onChange={(e) => onChange({ ...question, minLabel: e.target.value })}
              disabled={disabled}
              placeholder="例:そう思わない"
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>最大ラベル</span>
            <input
              value={question.maxLabel ?? ''}
              onChange={(e) => onChange({ ...question, maxLabel: e.target.value })}
              disabled={disabled}
              placeholder="例:とてもそう思う"
              className={inputCls}
            />
          </label>
        </div>
      );
    case 'rating':
      return (
        <label className="block max-w-xs">
          <span className={labelCls}>星の最大数</span>
          <select
            value={question.max}
            onChange={(e) =>
              onChange({ ...question, max: Number(e.target.value) as 3 | 5 | 7 | 10 })
            }
            disabled={disabled}
            className={inputCls}
          >
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={7}>7</option>
            <option value={10}>10</option>
          </select>
        </label>
      );
    case 'number':
      return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <label className="block">
            <span className={labelCls}>最小値(任意)</span>
            <input
              type="number"
              value={question.min ?? ''}
              onChange={(e) =>
                onChange({
                  ...question,
                  min: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              disabled={disabled}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>最大値(任意)</span>
            <input
              type="number"
              value={question.max ?? ''}
              onChange={(e) =>
                onChange({
                  ...question,
                  max: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              disabled={disabled}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>ステップ(任意)</span>
            <input
              type="number"
              step="any"
              value={question.step ?? ''}
              onChange={(e) =>
                onChange({
                  ...question,
                  step: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              disabled={disabled}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>単位(任意)</span>
            <input
              value={question.unit ?? ''}
              onChange={(e) => onChange({ ...question, unit: e.target.value })}
              disabled={disabled}
              placeholder="例:人 / 円"
              className={inputCls}
            />
          </label>
        </div>
      );
    case 'date':
    case 'time':
      return (
        <p className="text-xs text-slate-400">
          {question.type === 'date' ? '日付' : '時刻'}入力には追加設定はありません。
        </p>
      );
  }
}

export interface SurveyQuestionEditorProps {
  question: SurveyQuestion;
  index: number;
  total: number;
  disabled: boolean;
  /** 質問を丸ごと差し替える (type 変更 / 詳細編集)。 */
  onChange: (q: SurveyQuestion) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}

/**
 * 1 質問分の visual editor カード。SurveyEditPage / SurveyPresetEditPage 共通。
 * JSON 直接編集は「上級者向け」collapsible として温存 (parse 失敗時は onChange しない)。
 */
export function SurveyQuestionEditor({
  question,
  index,
  total,
  disabled,
  onChange,
  onMove,
  onRemove,
}: SurveyQuestionEditorProps) {
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  function patch(p: Partial<SurveyQuestion>) {
    onChange({ ...question, ...p } as SurveyQuestion);
  }

  function openJson() {
    setJsonText(JSON.stringify(question, null, 2));
    setJsonError(null);
    setJsonOpen((v) => !v);
  }

  function applyJson(text: string) {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text) as SurveyQuestion;
      if (typeof parsed !== 'object' || parsed === null || typeof parsed.id !== 'string') {
        throw new Error('id が string ではありません');
      }
      // id は URL / 並び順の key なので変更を許さない (旧 visual editor も同様)。
      parsed.id = question.id;
      setJsonError(null);
      onChange(parsed);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
          <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">Q{index + 1}</span>
          <span className="px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">
            {SURVEY_QUESTION_TYPE_LABELS[question.type]}
          </span>
          <code className="text-[10px] text-slate-400">{question.id}</code>
        </div>
        {!disabled && (
          <div className="flex gap-1 shrink-0">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onMove(-1)}
              disabled={index === 0}
              aria-label="上へ移動"
            >
              ↑
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onMove(1)}
              disabled={index === total - 1}
              aria-label="下へ移動"
            >
              ↓
            </Button>
            <Button size="sm" variant="danger" onClick={onRemove} aria-label="この質問を削除">
              削除
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="block sm:col-span-2">
            <span className={labelCls}>質問文</span>
            <input
              value={question.title}
              onChange={(e) => patch({ title: e.target.value })}
              disabled={disabled}
              placeholder="質問のタイトル"
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>タイプ</span>
            <select
              value={question.type}
              onChange={(e) =>
                onChange(changeQuestionType(question, e.target.value as SurveyQuestionType))
              }
              disabled={disabled}
              className={inputCls}
              aria-label="質問タイプ"
            >
              {SURVEY_QUESTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {SURVEY_QUESTION_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className={labelCls}>補足説明(任意)</span>
          <input
            value={question.description ?? ''}
            onChange={(e) => patch({ description: e.target.value })}
            disabled={disabled}
            placeholder="参加者向けの補足"
            className={inputCls}
          />
        </label>

        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={question.required}
            onChange={(e) => patch({ required: e.target.checked })}
            disabled={disabled}
            className="w-4 h-4"
          />
          必須
        </label>

        <QuestionTypeFields question={question} disabled={disabled} onChange={onChange} />

        {/* 上級者向け: 生 JSON で直接編集 (collapsible、既定は閉) */}
        <div className="mt-1 border-t border-slate-100 pt-2">
          <button
            type="button"
            onClick={openJson}
            className="text-[11px] text-slate-500 hover:text-slate-700 hover:underline"
            aria-expanded={jsonOpen}
          >
            {jsonOpen ? '▼' : '▶'} 上級者向け: 質問定義 JSON を直接編集
          </button>
          {jsonOpen && (
            <div className="mt-2">
              <textarea
                value={jsonText}
                onChange={(e) => applyJson(e.target.value)}
                rows={8}
                disabled={disabled}
                className="w-full px-3 py-2 border border-slate-300 rounded text-xs font-mono"
                spellCheck={false}
                aria-label="質問定義 JSON"
              />
              {jsonError && (
                <p className="text-[11px] text-danger-700 mt-1">JSON エラー: {jsonError}</p>
              )}
              {!jsonError && (
                <p className="text-[11px] text-slate-400 mt-1">
                  正しい JSON を入力すると visual editor に即時反映されます (id は固定)。
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
